import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, RotateCcw, KeyRound, Check, LogOut, Database, FileJson, ListChecks } from "lucide-react";
import AppBar from "../components/layout/AppBar";
import { api, errorMessage } from "../lib/api";
import { sortByOrder } from "../lib/matrix";
import { downloadBlob } from "../utils/exporters";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import RegionalPricesSection from "../components/admin/RegionalPricesSection";

const SS_KEY = "m365matrix.adminKey";

function PlanRow({ plan, adminKey, onSaved }) {
  const [teams, setTeams] = useState(plan.price?.teams ?? "");
  const [noTeams, setNoTeams] = useState(plan.price?.noTeams ?? "");
  const [busy, setBusy] = useState(false);
  const dirty = String(teams) !== String(plan.price?.teams ?? "") || String(noTeams) !== String(plan.price?.noTeams ?? "");

  const patch = async (body) => {
    setBusy(true);
    try {
      const updated = await api.admin.patchPlan(adminKey, plan.id, body);
      onSaved(updated);
      toast.success(`${plan.name} updated`);
    } catch (err) {
      toast.error("Update failed", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const savePrice = () => {
    const t = parseFloat(teams);
    const n = parseFloat(noTeams);
    if (Number.isNaN(t) || Number.isNaN(n)) return toast.error("Enter valid prices");
    return patch({ price: { teams: t, noTeams: n } });
  };

  return (
    <tr className="feature-row" data-testid={`admin-plan-${plan.id}`}>
      <td>
        <label className="filter-item" style={{ gap: 10 }}>
          <Switch
            checked={plan.enabled}
            disabled={busy}
            onCheckedChange={(v) => patch({ enabled: v })}
            aria-label={`Show ${plan.name}`}
            data-testid={`admin-enabled-${plan.id}`}
          />
          <span className={`status-dot ${plan.enabled ? "" : "off"}`} aria-hidden="true" />
          <span>{plan.enabled ? "Shown" : "Hidden"}</span>
        </label>
      </td>
      <td>
        <div className="plan-name">{plan.short}</div>
        <div className="plan-full">{plan.name}</div>
      </td>
      <td>
        <span className="chip">{plan.group}</span>
      </td>
      <td>
        <input className="price-input" value={teams} onChange={(e) => setTeams(e.target.value)} aria-label="Price with Teams" />
      </td>
      <td>
        <input className="price-input" value={noTeams} onChange={(e) => setNoTeams(e.target.value)} aria-label="Price without Teams" />
      </td>
      <td>
        <label className="filter-item">
          <Switch checked={plan.addon} disabled={busy} onCheckedChange={(v) => patch({ addon: v })} aria-label="Add-on pricing" />
          <span>Add-on</span>
        </label>
      </td>
      <td>
        <button type="button" className="tb-btn sm" disabled={!dirty || busy} onClick={savePrice} data-testid={`admin-save-${plan.id}`}>
          <Check size={13} /> Save
        </button>
      </td>
    </tr>
  );
}

export default function AdminPage() {
  const [adminKey, setAdminKey] = useState(() => window.sessionStorage.getItem(SS_KEY) || "");
  const [verified, setVerified] = useState(false);
  const [dataset, setDataset] = useState(null);
  const [importText, setImportText] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const fileRef = useRef(null);

  const plans = useMemo(() => sortByOrder(dataset?.plans || []), [dataset]);

  const refresh = async (key) => {
    const data = await api.admin.export(key);
    setDataset(data);
  };

  const verify = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.admin.verify(adminKey);
      window.sessionStorage.setItem(SS_KEY, adminKey);
      setVerified(true);
      await refresh(adminKey);
      toast.success("Admin access granted");
    } catch (err) {
      setVerified(false);
      toast.error("Access denied", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (adminKey && !verified) {
      api.admin
        .verify(adminKey)
        .then(() => {
          setVerified(true);
          return refresh(adminKey);
        })
        .catch(() => setVerified(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = () => {
    window.sessionStorage.removeItem(SS_KEY);
    setVerified(false);
    setDataset(null);
    setAdminKey("");
  };

  const exportJSON = () => {
    if (!dataset) return;
    downloadBlob(JSON.stringify(dataset, null, 1), `m365-matrix-${dataset.version || "export"}.json`, "application/json");
    toast.success("Dataset exported");
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportText(await file.text());
    toast(`Loaded ${file.name} - review and click Import`);
  };

  const importJSON = async () => {
    let parsed;
    try {
      parsed = JSON.parse(importText);
    } catch {
      return toast.error("Invalid JSON");
    }
    setBusy(true);
    try {
      const res = await api.admin.import(adminKey, parsed);
      toast.success("Dataset imported", {
        description: `${res.counts.plans} plans, ${res.counts.rows} rows, ${res.counts.groups} groups`,
      });
      setImportText("");
      await refresh(adminKey);
    } catch (err) {
      toast.error("Import failed", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const resetSeed = async () => {
    if (!confirmReset) {
      setConfirmReset(true);
      setTimeout(() => setConfirmReset(false), 4000);
      return;
    }
    setBusy(true);
    try {
      const res = await api.admin.reset(adminKey);
      toast.success("Dataset reset to bundled seed", { description: `${res.counts.rows} rows restored` });
      setConfirmReset(false);
      await refresh(adminKey);
    } catch (err) {
      toast.error("Reset failed", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const onPlanSaved = (updated) =>
    setDataset((prev) => (prev ? { ...prev, plans: prev.plans.map((p) => (p.id === updated.id ? updated : p)) } : prev));
  const onAddonSaved = (updated) =>
    setDataset((prev) => (prev ? { ...prev, addons: prev.addons.map((a) => (a.name === updated.name ? updated : a)) } : prev));

  const featureCount = (dataset?.rows || []).filter((r) => r.type === "feature").length;
  const enabledCount = plans.filter((p) => p.enabled).length;

  return (
    <div className="app-shell" style={{ height: "auto", minHeight: "100vh" }}>
      <AppBar version={dataset?.version} updatedAt={dataset?.updated_at} />
      <main className="app-main">
        {!verified ? (
          <form className="admin-card admin-login glass" onSubmit={verify} data-testid="admin-login">
            <h2>
              <KeyRound size={16} /> Admin access
            </h2>
            <p>Enter the admin key to manage the dataset, plan visibility and pricing.</p>
            <Input
              type="password"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              placeholder="Enter ADMIN_KEY"
              data-testid="admin-key-input"
            />
            <div className="admin-actions">
              <button type="submit" className="tb-btn primary" disabled={busy || !adminKey} data-testid="admin-key-submit">
                Unlock
              </button>
            </div>
            <p className="admin-hint">The key is configured as ADMIN_KEY in the backend environment.</p>
          </form>
        ) : (
          <>
            <div className="admin-grid">
              <section className="admin-card glass" data-testid="admin-dataset">
                <h2>
                  <Database size={16} /> Dataset
                </h2>
                <div className="toolbar-row" style={{ gap: 8 }}>
                  <span className="chip mono">v{dataset?.version || "-"}</span>
                  <span className="chip">
                    <strong>{dataset?.plans?.length || 0}</strong> plans
                  </span>
                  <span className="chip">
                    <strong>{enabledCount}</strong> shown
                  </span>
                  <span className="chip">
                    <strong>{featureCount}</strong> features
                  </span>
                  <span className="chip">
                    <strong>{dataset?.rows?.length || 0}</strong> rows
                  </span>
                </div>
                <p>Pricing effective {dataset?.pricing?.effective} - {dataset?.pricing?.note}.</p>
                <div className="admin-actions">
                  <button type="button" className="tb-btn" onClick={exportJSON} disabled={!dataset} data-testid="admin-export-btn">
                    <Download size={14} /> Export JSON
                  </button>
                  <button
                    type="button"
                    className={`tb-btn ${confirmReset ? "active" : ""}`}
                    onClick={resetSeed}
                    disabled={busy}
                    data-testid="admin-reset-btn"
                  >
                    <RotateCcw size={14} /> {confirmReset ? "Click again to confirm reset" : "Reset to bundled seed"}
                  </button>
                  <button type="button" className="tb-btn ghost" onClick={signOut} data-testid="admin-signout-btn">
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              </section>

              <section className="admin-card glass" data-testid="admin-import">
                <h2>
                  <FileJson size={16} /> Import dataset (JSON)
                </h2>
                <p>
                  Paste a full dataset (same shape as Export). Plans, groups, rows and pricing are replaced - use this to plug
                  in your own data source or regional price lists later.
                </p>
                <Textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder='{"groups":[...],"plans":[...],"rows":[...],"pricing":{...}}'
                  className="admin-textarea"
                  data-testid="admin-import-text"
                />
                <div className="admin-actions">
                  <button type="button" className="tb-btn" onClick={() => fileRef.current?.click()} data-testid="admin-file-btn">
                    <Upload size={14} /> Choose JSON file
                  </button>
                  <input ref={fileRef} type="file" accept="application/json,.json" hidden onChange={onFile} />
                  <button
                    type="button"
                    className="tb-btn primary"
                    onClick={importJSON}
                    disabled={busy || !importText.trim()}
                    data-testid="admin-import-btn"
                  >
                    <Upload size={14} /> Import
                  </button>
                </div>
              </section>
            </div>

            <section className="admin-card glass" style={{ padding: 0 }}>
              <h2 style={{ padding: "14px 16px 0" }}>
                <ListChecks size={16} /> Plans &amp; pricing
              </h2>
              <div className="admin-table-wrap">
                <table className="mx-table admin-table" data-testid="admin-plans-table">
                  <thead>
                    <tr>
                      <th>Visible</th>
                      <th>Plan</th>
                      <th>Group</th>
                      <th>With Teams</th>
                      <th>No Teams</th>
                      <th>Type</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((plan) => (
                      <PlanRow key={plan.id} plan={plan} adminKey={adminKey} onSaved={onPlanSaved} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {dataset && <RegionalPricesSection dataset={dataset} plans={plans} adminKey={adminKey} onPlanSaved={onPlanSaved} onAddonSaved={onAddonSaved} />}
          </>
        )}
      </main>
    </div>
  );
}
